import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaUserTie, FaCalendarAlt, FaDollarSign, FaTasks } from 'react-icons/fa';
import '../pages_premium.css';

function HiremateTaskDetailView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchTask = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/hiremate-tasks/${id}`);
            if (res.data) {
                setTask(res.data);
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
            toast.error('Failed to load task details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTask();
    }, [id]);

    const formatDateTimeSingleLine = (taskData) => {
        if (!taskData) return 'Flexible';
        let dateStr = '';
        if (taskData.date_type === 'flexible') {
            dateStr = 'Flexible';
        } else if (taskData.task_date) {
            dateStr = taskData.task_date;
        }

        let timeStr = taskData.time_of_day || '';
        if (dateStr && timeStr) return `${dateStr} • ${timeStr}`;
        return dateStr || timeStr || 'Flexible';
    };

    if (loading) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5 text-secondary">
                    Loading task details...
                </div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5">
                    <p style={{ color: '#ef4444', fontWeight: '700' }}>Task Not Found</p>
                    <button
                        onClick={() => navigate('/hiremate-tasks')}
                        style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#334155', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                        Back to Tasks List
                    </button>
                </div>
            </div>
        );
    }

    const poster = task.Poster || {};

    return (
        <div className="page-premium-wrapper hiremate-task-view-page">
            <div className="page-premium-content">

                {/* Back Button */}
                <div style={{ marginBottom: '16px' }}>
                    <button
                        onClick={() => navigate('/hiremate-tasks')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    >
                        <FaArrowLeft /> Back to Tasks List
                    </button>
                </div>

                {/* Hero Task Banner */}
                <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '20px',
                    padding: '24px 28px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px' }}>
                                {task.category || 'User Task'}
                            </span>
                            <span style={{
                                backgroundColor: task.status === 'open' || task.status === 'Open' ? '#dcfce7' : '#fee2e2',
                                color: task.status === 'open' || task.status === 'Open' ? '#15803d' : '#b91c1c',
                                fontSize: '12px',
                                fontWeight: '700',
                                padding: '3px 10px',
                                borderRadius: '10px',
                                textTransform: 'capitalize'
                            }}>
                                {task.status || 'open'}
                            </span>
                        </div>
                        <h2 style={{ margin: 0, fontWeight: '800', color: '#0f172a', fontSize: '1.5rem' }}>
                            {task.title}
                        </h2>
                        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaUserTie style={{ color: '#0ab68e' }} /> Posted by {poster.name || 'User'} ({poster.email || '-'})
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <span style={{
                            backgroundColor: '#f1f5f9',
                            color: '#0f172a',
                            padding: '10px 18px',
                            borderRadius: '16px',
                            fontWeight: '800',
                            fontSize: '16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <FaDollarSign style={{ color: '#0ab68e' }} /> ${parseFloat(task.budget || 0).toFixed(2)} (Fixed Offer)
                        </span>
                    </div>
                </div>

                {/* Main Details Grid */}
                <div className="admin-2col-grid" style={{ marginBottom: '24px' }}>

                    {/* Task Description */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaTasks style={{ color: '#0ab68e' }} /> Task Description & Instructions
                        </h5>
                        <p style={{ color: '#475569', fontSize: '14px', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-line' }}>
                            {task.description || 'No description provided.'}
                        </p>
                    </div>

                    {/* Task Poster User Details */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaUserTie style={{ color: '#4f46e5' }} /> Task Poster User Information
                        </h5>
                        <div style={{ fontSize: '14px', color: '#334155', spaceY: '6px' }}>
                            <p style={{ margin: '4px 0' }}><strong>Name:</strong> {poster.name || 'Unknown User'}</p>
                            <p style={{ margin: '4px 0' }}><strong>Email:</strong> {poster.email || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>Phone:</strong> {poster.phone || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>User Role:</strong> {poster.role || 'student'}</p>
                        </div>
                    </div>

                </div>

                {/* Schedule & Location */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaCalendarAlt style={{ color: '#0ab68e' }} /> Task Schedule & Location
                    </h5>
                    <div className="admin-2col-grid">
                        <div>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Date & Time:</strong> {formatDateTimeSingleLine(task)}</p>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Date Preference:</strong> {task.date_type === 'on_date' ? 'Specific Date' : 'Flexible'}</p>
                        </div>
                        <div>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Location Type:</strong> {task.location_type === 'online' ? 'Online / Remote' : 'In Person'}</p>
                            {task.suburb && <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Suburb / Address:</strong> {task.suburb}</p>}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default HiremateTaskDetailView;
